/**
 * Volet « compte » des Réglages — passkeys, clé de secours, session (étape 4).
 *
 * ⚠️ Extrait de `SettingsScreen` aux retours de l'étape 10, quand les Réglages
 * sont passés en sommaire et sous-pages : chaque volet est désormais un
 * composant autonome, monté seulement quand on le regarde. Conséquence
 * appréciable ici — la requête `/api/auth/account` ne part plus à chaque
 * ouverture de l'onglet Réglages, mais seulement quand on entre dans le compte.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { PasskeySummary } from '../../../../shared/types';
import {
  addPasskey,
  fetchAccount,
  guessDeviceLabel,
  logout,
  removePasskey,
  rotateRecoveryKey,
} from '../../api/auth';
import { ApiError, readError } from '../../api/client';
import { Button, ErrorNote, Field, Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { TOKENS } from '../../components/ui/tokens';
import { purgePersistedCache } from '../../lib/persist';
import { forgetTabs } from '../../lib/tabMemory';
import { RecoveryKeyPanel } from '../auth/parts';

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) {
    if (error.name === 'NotAllowedError') return 'Opération annulée.';
    return error.message;
  }
  return 'Échec inattendu.';
}

function formatDate(epochMs: number | null): string {
  if (epochMs === null) return 'jamais';
  return new Date(epochMs).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
}

function PasskeyRow({
  passkey,
  onRemove,
  removing,
}: {
  passkey: PasskeySummary;
  onRemove: () => void;
  removing: boolean;
}) {
  return (
    <li
      style={{
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.75rem 0',
        borderBottom: `1px solid ${TOKENS.border}`,
      }}
    >
      <div>
        <div>{passkey.deviceLabel ?? 'Appareil sans nom'}</div>
        <div style={{ color: TOKENS.textQuiet, fontSize: '0.8rem' }}>
          créée le {formatDate(passkey.createdAt)} · dernier usage {formatDate(passkey.lastUsedAt)}
        </div>
      </div>
      <Button variant="quiet" onClick={onRemove} disabled={removing}>
        Retirer
      </Button>
    </li>
  );
}

export function AccountSection() {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(guessDeviceLabel);
  const [rotated, setRotated] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const account = useQuery({ queryKey: ['auth', 'account'], queryFn: fetchAccount });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['auth'] });

  const add = useMutation({
    mutationFn: () => addPasskey(label.trim()),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: removePasskey,
    onSuccess: refresh,
  });

  const rotate = useMutation({
    mutationFn: rotateRecoveryKey,
    onSuccess: (result) => {
      setAcknowledged(false);
      setRotated(result.recoveryKey);
      void refresh();
    },
  });

  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: (status) => {
      // ⚠️ Ne **pas** utiliser `queryClient.clear()` ici. Il détruit aussi la
      // requête d'état dont dépend la garde ; l'observateur monté reste alors
      // accroché à une requête supprimée, plus rien ne le notifie, et
      // l'application continue de s'afficher en enchaînant les 401. Constaté
      // en test de bout en bout.
      //
      // On pose donc d'abord le nouvel état — ce qui bascule la garde sur
      // l'écran de connexion — puis on retire tout le reste, c'est-à-dire les
      // données de la session qui vient d'être fermée.
      queryClient.setQueryData(['auth', 'status'], status);
      // La mémoire de navigation appartenait à la session qui se ferme :
      // sans cela, la suivante rouvrirait la bibliothèque sur la recette
      // que la précédente consultait.
      forgetTabs();
      queryClient.removeQueries({
        predicate: (query) =>
          !(query.queryKey[0] === 'auth' && query.queryKey[1] === 'status'),
      });
      /*
       * 🔴 **Et le cache hors ligne, qui vit sur le disque** (V2, étape 2 —
       * `ARCHITECTURE_V2.md` §3.2). Il contient le journal alimentaire et
       * l'historique de poids : vider le cache mémoire ci-dessus ne suffit
       * pas, le contenu survivrait dans IndexedDB jusqu'à la session suivante.
       * Même règle que `forgetTabs()`, avec une portée plus longue — celle-ci
       * survit à la fermeture de l'application.
       *
       * ⚠️ **L'ordre compte, et il est tenu par construction.** L'abonnement de
       * persistance réécrit le cache dès qu'il change, avec un léger différé :
       * une écriture peut donc partir après cette purge. C'est sans
       * conséquence parce que le cache mémoire vient d'être vidé — ce qui reste
       * est `['auth', 'status']`, c'est-à-dire exactement ce qu'on veut voir
       * persisté : « personne n'est connecté ».
       */
      void purgePersistedCache();
      /*
       * 🔴 **La file de synchronisation N'EST PAS purgée ici, et il ne faut
       * jamais l'y ajouter** (V2 §3.3, étape 3). C'est délibéré, et c'est même
       * la raison pour laquelle elle vit dans une base IndexedDB distincte de
       * `nutri-cache` : « rien ne la vide sans accusé de réception du serveur ».
       *
       * Le cache de lecture n'est qu'une copie de ce que le serveur possède
       * déjà — le jeter ne perd rien. Une opération en file, elle, **n'existe
       * nulle part ailleurs** : c'est une saisie que l'utilisateur a faite et
       * que le serveur n'a pas encore vue. La supprimer parce qu'on se
       * déconnecte détruirait la journée qu'on vient de loguer en salle, en
       * silence — exactement le défaut que le hors ligne existe pour empêcher.
       *
       * ⚠️ Les deux bases séparées rendent l'erreur difficile à commettre :
       * `purgePersistedCache()` ne peut pas atteindre `nutri-sync`, quoi qu'on
       * écrive ici. C'est une garantie de structure, pas de vigilance.
       */
    },
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1rem', margin: 0 }}>Passkeys</h2>

        {account.isPending && <Hint>Chargement…</Hint>}
        <ReadNote error={readError(account)} quoi="le compte" />

        {account.data && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {account.data.passkeys.map((passkey) => (
              <PasskeyRow
                key={passkey.id}
                passkey={passkey}
                removing={remove.isPending}
                onRemove={() => {
                  remove.mutate(passkey.id);
                }}
              />
            ))}
          </ul>
        )}

        {remove.isError && <ErrorNote>{describe(remove.error)}</ErrorNote>}

        <Field label="Nom du nouvel appareil" value={label} onChange={setLabel} />
        {add.isError && <ErrorNote>{describe(add.error)}</ErrorNote>}
        <Button
          disabled={label.trim() === '' || add.isPending}
          onClick={() => {
            add.mutate();
          }}
        >
          {add.isPending ? 'Enregistrement…' : 'Ajouter une passkey'}
        </Button>
        <Hint>
          À faire une fois depuis chaque appareil — l’iPhone et l’ordinateur ont chacun la leur.
        </Hint>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1rem', margin: 0 }}>Clé de secours</h2>

        {rotated === null ? (
          <>
            <Hint>
              En vigueur depuis le {formatDate(account.data?.recoveryCreatedAt ?? null)}. La
              renouveler annule immédiatement la précédente.
            </Hint>
            {rotate.isError && <ErrorNote>{describe(rotate.error)}</ErrorNote>}
            <Button
              variant="quiet"
              disabled={rotate.isPending}
              onClick={() => {
                rotate.mutate();
              }}
            >
              {rotate.isPending ? 'Génération…' : 'Renouveler la clé de secours'}
            </Button>
          </>
        ) : (
          <>
            <RecoveryKeyPanel
              recoveryKey={rotated}
              acknowledged={acknowledged}
              onAcknowledge={setAcknowledged}
            />
            <Button
              disabled={!acknowledged}
              onClick={() => {
                setRotated(null);
              }}
            >
              Terminé
            </Button>
          </>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1rem', margin: 0 }}>Session</h2>
        <Button
          variant="quiet"
          disabled={signOut.isPending}
          onClick={() => {
            signOut.mutate();
          }}
        >
          Se déconnecter
        </Button>
      </div>
    </div>
  );
}
